import * as React from "react";
import moment from "moment-timezone";
import { CalendarDays } from "lucide-react";

import { CommonDestinationTimezone, cn } from "../../lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import { Calendar, DateRange, SelectRangeEventHandler } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export function DatePicker({
    id,
    disabled,
    value: date,
    onChange: setDate,
    tabIndex,
    placeholder,
    className,
    minDate,
    maxDate
}: {
    id?: string,
    disabled?: boolean,
    placeholder?: string;
    value?: Date,
    onChange: (date: Date | undefined) => void,
    tabIndex?: number;
    className?: string,
    minDate?: Date,
    maxDate?: Date
}) {
  const [stringDate, setStringDate] = React.useState<string>("")
  const [errorMessage, setErrorMessage] = React.useState<string>("")

    React.useEffect(() => {
        let computedDate = date ? moment(date).format("MM/DD/YYYY") : "";
        if (stringDate !== computedDate) {
            setStringDate(computedDate);
        }
    }, [date]);

  const fromDate = minDate || moment().subtract(5, "years").startOf("day").toDate();
  const toDate = moment().add(5, "years").endOf("day").toDate();

  const fromYear = moment().subtract(5, "years").startOf("day").year();
  const toYear = moment().add(5, "years").endOf("day").year();

  return (
      <Popover>
          <div className="relative w-[280px]">
              <Input
                  id={id}
                  data-testid={id ? `form-input-${id}` : undefined}
                  disabled={disabled}
                  className={className}
                  tabIndex={tabIndex}
                  type="string"
                  value={stringDate}
                  placeholder={placeholder || "MM/DD/YYYY"}
                  onChange={(e) => {
                      setStringDate(e.target.value);

                      if (e.target.value?.length !== 10) {
                          return;
                      }

                      const parsedDate = moment(e.target.value);
                      if (!parsedDate.isValid()) {
                          setErrorMessage("Invalid Date");
                          setDate(undefined);
                      } else {
                          setErrorMessage("");
                          setDate(parsedDate.toDate());
                      }
                  }}
                onBlur={() => {
                  if (!stringDate) {
                    setDate(undefined)
                  }
                }}
              />
              <PopoverTrigger asChild>
                  <Button
                      disabled={disabled}
                      variant={"outline"}
                      className={cn(
                          "font-normal absolute right-0 translate-y-[-50%] top-[50%] rounded-l-none",
                          !date && "text-muted-foreground"
                      )}
                  >
                      <CalendarDays className="w-4 h-4" />
                  </Button>
              </PopoverTrigger>
          </div>
          <PopoverContent className="w-auto p-0">
              <Calendar
                    mode="single"
                    captionLayout="dropdown-buttons"
                    fromDate={fromDate}
                    toDate={toDate}
                    fromYear={fromYear}
                    toYear={toYear}
                    disabled={(date) => {
                        if (minDate && date < minDate) return true;
                        if (maxDate && date > maxDate) return true;
                        return false;
                    }}
                    selected={date ? moment.tz(date, CommonDestinationTimezone).toDate(): undefined}
                    onSelect={(selectedDate) => {
                      if (!selectedDate) {
                        setDate(undefined);
                        setStringDate("");
                        return;
                      }
                      setDate(selectedDate)

                      setStringDate(moment(selectedDate).format("MM/DD/YYYY"))
                      setErrorMessage("")
                  }}
                  defaultMonth={date ? moment(date).toDate(): undefined}
                  initialFocus
              />
          </PopoverContent>
      </Popover>
  )
}
